import React, { useEffect, useMemo, useState } from 'react'
import { OptimizationLogEntry } from '@/api'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { InputText } from 'primereact/inputtext'
import { Badge } from 'primereact/badge'
import { ToggleButton } from 'primereact/togglebutton'

interface OptimizationLogsPanelProps {
  logs: OptimizationLogEntry[]
  defaultFilter?: string
}

type LogLevel = OptimizationLogEntry['level']
const { ERROR, WARNING, INFO, DEBUG } = OptimizationLogEntry.level

const LEVEL_COLORS: Record<LogLevel, string> = {
  [ERROR]: 'danger',
  [WARNING]: 'warning',
  [INFO]: 'info',
  [DEBUG]: 'secondary',
}

const LEVELS: LogLevel[] = [ERROR, WARNING, INFO, DEBUG]

const formatTimestamp = (ts: string) => {
  try {
    const d = new Date(ts)
    return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    return ts
  }
}

const OptimizationLogsPanel: React.FC<OptimizationLogsPanelProps> = ({ logs, defaultFilter = '' }) => {
  const [textFilter, setTextFilter] = useState(defaultFilter)
  const [enabledLevels, setEnabledLevels] = useState<Set<LogLevel>>(new Set([ERROR, WARNING, INFO]))

  useEffect(() => {
    setTextFilter(defaultFilter)
  }, [defaultFilter])

  const levelCounts = useMemo(() => {
    const counts: Record<LogLevel, number> = { [ERROR]: 0, [WARNING]: 0, [INFO]: 0, [DEBUG]: 0 }
    for (const log of logs) {
      counts[log.level] = (counts[log.level] ?? 0) + 1
    }
    return counts
  }, [logs])

  const filteredLogs = useMemo(() => {
    const idMatch = textFilter.match(/^id:(\S+)/i)
    const itemIdFilter = idMatch ? idMatch[1] : null
    const plainTextFilter = itemIdFilter ? textFilter.slice(idMatch![0].length).trim() : textFilter

    return logs.filter(log => {
      if (!enabledLevels.has(log.level)) return false
      if (itemIdFilter && !log.message.includes(`item=${itemIdFilter}`)) return false
      if (plainTextFilter && !log.message.toLowerCase().includes(plainTextFilter.toLowerCase())) return false
      return true
    })
  }, [logs, enabledLevels, textFilter])

  const toggleLevel = (level: LogLevel) => {
    setEnabledLevels(prev => {
      const next = new Set(prev)
      if (next.has(level)) {
        next.delete(level)
      } else {
        next.add(level)
      }
      return next
    })
  }

  return (
    <div className="flex flex-column gap-2">
      <div className="flex flex-row flex-wrap gap-2 align-items-center">
        {LEVELS.map(level => (
          <ToggleButton
            key={level}
            checked={enabledLevels.has(level)}
            onChange={() => toggleLevel(level)}
            onLabel={`${level} (${levelCounts[level]})`}
            offLabel={`${level} (${levelCounts[level]})`}
            className={`p-button-sm p-button-outlined p-button-${LEVEL_COLORS[level]}`}
            style={{ minWidth: '7rem' }}
          />
        ))}
        <InputText
          value={textFilter}
          onChange={e => setTextFilter(e.target.value)}
          placeholder="メッセージ検索... (id:品目IDでフィルタ)"
          className="p-inputtext-sm flex-1"
          style={{ minWidth: '12rem' }}
        />
        <span className="text-600 text-sm">{filteredLogs.length}件</span>
      </div>
      <DataTable
        value={filteredLogs}
        scrollable
        scrollHeight="300px"
        virtualScrollerOptions={{ itemSize: 36 }}
        emptyMessage="ログがありません"
        className="text-sm"
        size="small"
      >
        <Column
          field="timestamp"
          header="時刻"
          style={{ width: '7rem', minWidth: '7rem' }}
          body={(row: OptimizationLogEntry) => (
            <span className="text-600 text-xs">{formatTimestamp(row.timestamp)}</span>
          )}
        />
        <Column
          field="level"
          header="レベル"
          style={{ width: '6rem', minWidth: '6rem' }}
          body={(row: OptimizationLogEntry) => (
            <Badge value={row.level} severity={LEVEL_COLORS[row.level] as 'danger' | 'warning' | 'info' | 'secondary'} />
          )}
        />
        <Column
          field="message"
          header="メッセージ"
          body={(row: OptimizationLogEntry) => (
            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {row.message}
            </span>
          )}
        />
      </DataTable>
    </div>
  )
}

export default OptimizationLogsPanel
